import { useAtomValue } from 'jotai'
import { ReactNode, useEffect, useMemo, useState } from 'react'

import {
  BalancesAndAllowancesUpdater,
  balancesWatcherHealthAtom,
  BalancesWatcherUpdater,
  PRIORITY_TOKENS_REFRESH_INTERVAL,
  PriorityTokensUpdater,
} from '@cowprotocol/balances-and-allowances'
import { isEvmChain, isSolanaChain, SupportedChainId } from '@cowprotocol/cow-sdk'
import { useWalletInfo } from '@cowprotocol/wallet'

import { useBalancesAccountForChain } from 'entities/balancesContext/useBalancesAccountForChain'

import { Field } from 'legacy/state/types'

import { useSelectTokenWidgetState, useSourceChainId } from 'modules/tokensList'
import { usePriorityTokenAddresses } from 'modules/trade'

import { useBridgeCustomTokensForChain } from '../hooks/useBridgeCustomTokensForChain'
import { useConfirmedTransactionsTrigger } from '../hooks/useConfirmedTransactionsTrigger'
import { useOrdersFilledEventsTrigger } from '../hooks/useOrdersFilledEventsTrigger'

export function CommonPriorityBalancesAndAllowancesUpdater(): ReactNode {
  const { account, chainId: walletChainId } = useWalletInfo()
  const selectorSourceChain = useSourceChainId()
  // Cross-chain swaps are not available from Solana, so other chains' balances are never needed there
  const { chainId: sourceChainId, source: sourceChainSource } = isSolanaChain(walletChainId)
    ? { chainId: walletChainId, source: 'wallet' as const }
    : selectorSourceChain
  // Bridge buy-tokens are only meaningful for the output/buy selector. The input/sell selector on a non-wallet chain
  // also yields source='selector' but must keep the normal token-list + user-custom-tokens session.
  const { field } = useSelectTokenWidgetState()
  const isBridgeMode = sourceChainSource === 'selector' && field === Field.OUTPUT
  const balancesAccount = useBalancesAccountForChain(sourceChainId)

  const priorityTokenAddresses = usePriorityTokenAddresses()
  const priorityTokenAddressesAsArray = useMemo(() => {
    return Array.from(priorityTokenAddresses.values())
  }, [priorityTokenAddresses])
  const priorityTokenCount = priorityTokenAddressesAsArray.length

  const [skipFirstPriorityUpdate, setSkipFirstPriorityUpdate] = useState(true)

  /**
   * Reset skipFirstPriorityUpdate on every network change
   */
  useEffect(() => {
    setSkipFirstPriorityUpdate(true)
  }, [sourceChainId])

  /**
   * Stop skipping priority tokens updating once wallet is connected and there are some priority tokens
   */
  useEffect(() => {
    if (!account || !priorityTokenCount) return

    const timeout = setTimeout(() => {
      setSkipFirstPriorityUpdate(false)
    }, PRIORITY_TOKENS_REFRESH_INTERVAL)

    return () => {
      clearTimeout(timeout)
    }
  }, [account, priorityTokenCount])

  // Both counters only ever increase, so their sum changes whenever either fires.
  const refreshTrigger = useOrdersFilledEventsTrigger() + useConfirmedTransactionsTrigger()

  const bridgeTokenList = useBridgeCustomTokensForChain(sourceChainId)

  const { isRecovering: isWatcherRecovering } = useAtomValue(balancesWatcherHealthAtom)
  const isWatcherActive = isEvmChain(sourceChainId)
  // Mount the multicall stack when:
  // - the watcher isn't running at all (bw flag off, or non-EVM chain), OR
  // - the watcher is in recovery — sticky from the first failure until the next
  //   successful snapshot, so retry transitions (Connecting/Connected/Fallback)
  //   don't briefly unmount it and leave a balance gap.
  const needsMulticallStack = !isWatcherActive || isWatcherRecovering
  const multicallStack = needsMulticallStack ? (
    <>
      <PriorityTokensUpdater
        // We can and should save one RPC call at the very beginning
        // Since regular BalancesAndAllowancesUpdater will update all tokens (including priority tokens)
        // We can skip first update for PriorityTokensUpdater
        account={skipFirstPriorityUpdate ? undefined : balancesAccount}
        chainId={sourceChainId}
        tokenAddresses={priorityTokenAddressesAsArray}
      />
      <BalancesAndAllowancesUpdater
        account={balancesAccount}
        chainId={sourceChainId}
        excludedTokens={priorityTokenAddresses}
        refreshTrigger={refreshTrigger}
      />
    </>
  ) : null

  if (isWatcherActive) {
    return (
      <>
        <BalancesWatcherUpdater
          account={balancesAccount}
          chainId={sourceChainId as number as SupportedChainId}
          isBridgeMode={isBridgeMode}
          bridgeTokenList={bridgeTokenList}
        />
        {multicallStack}
      </>
    )
  }

  return multicallStack
}
